import { useState } from "react";
import { X, Trash2, Loader2 } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  standard: "Standard Lease",
  pro: "Professional Lease",
  unlimited: "Unlimited Covenant",
};

export default function CartDrawer() {
  const { items, removeItem, total, isOpen, closeCart, clearCart } = useCart();
  const [email, setEmail] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);

  const handleCheckout = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setCheckingOut(true);
    try {
      // Persist the email locally so SuccessPage can pass it back to the
      // download endpoint (which now requires email-binding against the order).
      try {
        sessionStorage.setItem("ss_checkout_email", email.trim().toLowerCase());
      } catch (_) {
        /* private mode / disabled storage — non-fatal */
      }
      const res = await axios.post(`${API}/checkout/create-session`, {
        items: items.map((i) => ({
          beat_id: i.beat.id,
          license_tier: i.licenseTier,
        })),
        customer_email: email,
        origin_url: window.location.origin,
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Checkout failed. Please try again.");
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="chrome-panel backdrop-blur-xl w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#1A1A1A]">
          <SheetTitle
            className="text-xl text-[#EAEAEA]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Your Cart
          </SheetTitle>
          <SheetDescription className="text-[#737373] text-sm">
            {items.length} {items.length === 1 ? "item" : "items"}
          </SheetDescription>
        </SheetHeader>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="text-center py-16 text-[#737373]">
              <p className="text-base">Your cart is empty</p>
              <p className="text-sm mt-2">Browse the catalog and add beats.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={`${item.beat.id}-${item.licenseTier}`}
                  data-testid={`cart-item-${item.beat.id}`}
                  className="flex items-center gap-3 p-3 chrome-panel rounded-xl"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#EAEAEA] font-medium truncate">{item.beat.title}</p>
                    <p className="text-xs text-gold mt-0.5">{TIER_LABELS[item.licenseTier]}</p>
                  </div>
                  <p className="text-sm text-[#EAEAEA] font-medium">${item.price.toFixed(2)}</p>
                  <button
                    data-testid={`remove-item-${item.beat.id}`}
                    onClick={() => removeItem(item.beat.id, item.licenseTier)}
                    className="text-[#737373] hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-[#1A1A1A] space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#A1A1A1]">Total</span>
              <span className="text-xl font-semibold text-gold">${total.toFixed(2)}</span>
            </div>

            <Input
              data-testid="checkout-email-input"
              type="email"
              placeholder="Your email for delivery"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-ph-no-capture
              className="ph-no-capture bg-transparent border-0 border-b border-[#1A1A1A] rounded-none px-0 text-sm text-[#EAEAEA] placeholder:text-[#737373] focus-visible:ring-0 focus-visible:border-gold"
            />

            <button
              data-testid="checkout-btn"
              onClick={handleCheckout}
              disabled={checkingOut}
              className="metal-gold-surface w-full py-3.5 font-semibold text-sm tracking-widest uppercase rounded-full transition-all hover:brightness-105 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {checkingOut ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                "Proceed to Checkout"
              )}
            </button>

            <button
              data-testid="clear-cart-btn"
              onClick={clearCart}
              className="w-full text-center text-xs text-[#737373] hover:text-red-400 transition-colors"
            >
              Clear Cart
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
